import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { useAddLabelIntake, useGetPatient, useGetTwilioToken, useListObjectives, useUploadStream, useGetPatients } from 'web/services/api/queries';
import { Label } from 'web/model/Label';
import { DataPackageBuilder } from 'web/utils/DataPackageBuilder';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import useEnsureDevice from 'web/hooks/device/useEnsureDevice';
import { Patient } from 'web/model/Patient';
import { formatPhoneNumber } from 'web/utils/phoneUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchablePatientSelector } from 'web/components/SearchablePatientSelector';

export const AddLabelPhoneCall = ({ label, cancelAdd }: {
    label: Label,
    cancelAdd: () => void,
}) => {
    const { deviceSerial } = useEnsureDevice();
    const [currentPatientId, setCurrentPatientId] = useState<string>(label.patientId ?? '');
    const { refetch: refetchTwilioToken } = useGetTwilioToken(currentPatientId || '');
    const { mutate: addLabelIntake } = useAddLabelIntake();
    const [device, setDevice] = useState<Device | undefined>(undefined);
    const [phoneNumber, setPhoneNumber] = useState('+1 ' + (label.metadata?.phoneNumber ?? ''));
    const [inCall, setInCall] = useState(false);
    const [recordedChunks] = useState<Blob[]>([]);
    const recorder = useRef<RecordRTC | null>(null);
    const uploadStream = useUploadStream();
    const streamStartTime = useRef<number | null>(null);
    const streamEndTime = useRef<number | null>(null);
    const labelStartTime = useRef<number | null>(null);
    const labelEndTime = useRef<number | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const localMediaStream = useRef<MediaStream | undefined>(undefined);
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { refetch } = useListObjectives(currentPatientId || '');
    const { data: patientsData } = useGetPatients({}, 0, 1000);
    const patients = patientsData?.data?.filter(p => !p.isDefault) || [];
    const getPatient = useGetPatient.func();
    const [recordingTime, setRecordingTime] = useState(0);
    const recordingInterval = useRef<NodeJS.Timeout | null>(null);

    const sampleRate = 16000;
    const phoneAudioRecording = 512;

    const initializeDevice = async () => {
        const { data } = await refetchTwilioToken();
        if (data && data.token && data.callerId) {
            const newDevice = new Device(data.token, {
                codecPreferences: ['opus', 'pcmu'] as any,
            });

            newDevice.on('registered', () => {
                console.log('Device Ready');
                console.log('Device Identity after registration:', newDevice.identity);
                setDevice(newDevice); // Set the device after registration
            });

            newDevice.on('error', (error) => {
                console.error('Device Error:', error);
                setErrorMessage('An error occurred while setting up the call. Please try again.'); // Generic error message
            });

            newDevice.on('disconnect', () => {
                console.log('Call ended');
                setInCall(false);
                handleCallEnd();
            });

            newDevice.register(); // Explicitly register the device

            return { device: newDevice, identity: data.identity, callerId: data.callerId };
        } else {
            console.error('Failed to fetch call token');
            setErrorMessage('An error occurred while fetching the call token. Please try again.'); // Generic error message
            return null;
        }
    };

    const processPCM = useCallback((blob: Blob) => {
        blob.arrayBuffer().then(buffer => {
            const int16Array = new Int16Array(buffer);
            var calcSampleRate = audioContextRef.current?.sampleRate;
            if (calcSampleRate == null) {
                calcSampleRate = sampleRate;
            }
            const calculatedDiff = Math.round((int16Array.length / calcSampleRate) * 1000);

            if (streamStartTime.current == null) {
                streamStartTime.current = Date.now() - calculatedDiff;
                labelStartTime.current = streamStartTime.current;
            }

            if (streamEndTime.current != null) {
                streamStartTime.current = streamEndTime.current;
            }

            streamEndTime.current = streamStartTime.current + calculatedDiff;
            labelEndTime.current = streamEndTime.current + calculatedDiff;

            let dataPackage = new DataPackageBuilder(BigInt(streamStartTime.current), BigInt(streamEndTime.current + 5), [phoneAudioRecording]);
            int16Array.forEach(sample => dataPackage.addSample(sample));

            setTimeout(async () => {
                try {
                    const fileContents = dataPackage.build();
                    await uploadStream.mutateAsync({ deviceSerial, fileContents });
                } catch (error) {
                    console.error('Exception in uploading stream', error);
                    setErrorMessage('An error occurred while uploading the stream. Please try again.'); // Generic error message
                }
            }, 0);

        }).catch(error => {
            console.error('Error processing audio', error);
            setErrorMessage('An error occurred while processing the audio. Please try again.'); // Generic error message
        });
    }, [uploadStream, deviceSerial]);

    const handleDataAvailable = useCallback((blob: Blob) => {
        if (blob.size > 0) {
            processPCM(blob);
        }
    }, [processPCM]);

    const makePhoneCall = async (device: Device, identity: string, callerId: string) => {
        if (!device || !identity) {
            console.error('Device not initialized or identity missing');
            setErrorMessage('An error occurred while setting up the call. Please try again.'); // Generic error message
            return;
        }

        streamStartTime.current = null;
        labelStartTime.current = null;
        streamEndTime.current = null;
        localMediaStream.current = undefined;

        console.log(`identity: ${identity}`);
        const params = {
            To: phoneNumber, // Use the phone number from the input field
            callingDeviceIdentity: identity,
            callerId: callerId
        };

        const call: Call = await device.connect({ params });

        call.on('accept', () => {
            console.log('Call in progress');
            setInCall(true);

            // Handle audio data
            const audioContext = new AudioContext({ sampleRate: 16000 });
            audioContextRef.current = audioContext; // Store the audio context reference
            const destination = audioContext.createMediaStreamDestination();

            const localStream = call.getLocalStream();
            if (localStream) {
                const localSource = audioContext.createMediaStreamSource(localStream);
                localSource.connect(destination);
                console.log('Local source connected to destination');
            }

            call.on('volume', (inputVolume, outputVolume) => {
                if (outputVolume > 0) {
                    if (localMediaStream.current == null) {
                        localMediaStream.current = call.getRemoteStream();
                        if (localMediaStream.current) {
                            const remoteSource = audioContext.createMediaStreamSource(localMediaStream.current);
                            remoteSource.connect(destination);
                            // console.log('Remote source connected to destination');
                        }
                    }
                }
            });

            const timeSliceInSeconds = 2;
            const timeSlice = timeSliceInSeconds * 1000;
            recorder.current = new RecordRTC(destination.stream, {
                type: 'audio',
                mimeType: 'audio/wav',
                recorderType: StereoAudioRecorder,
                desiredSampRate: sampleRate,
                numberOfAudioChannels: 1,
                timeSlice: timeSlice,
                ondataavailable: handleDataAvailable
            });

            recorder.current.startRecording();
            streamRef.current = destination.stream;
        });

        call.on('error', (error) => {
            console.error('Call error', error);
            setErrorMessage('An error occurred during the call. Please try again.'); // Generic error message
        });

        call.on('disconnect', () => {
            console.log('Call ended');
            setInCall(false);
            handleCallEnd();
        });

        call.on('cancel', () => {
            console.log('Call canceled');
            setInCall(false);
            handleCallEnd();
        });
    };

    const handleCallEnd = () => {
        if (recorder.current) {
            recorder.current.stopRecording(() => {
                const blob = recorder.current?.getBlob();
                if (blob) {
                    handleDataAvailable(blob);
                }
            });
        }
        streamRef.current?.getTracks().forEach(track => track.stop());

        const recordingStopTime = Date.now();
        const newLabel = new Label({
            ...label,
            fromTime: labelStartTime.current ?? recordingStopTime,
            toTime: labelEndTime.current ?? recordingStopTime,
            color: "PURPLE",
            description: "",
            metadata: {
                phoneNumber,
            },
        });

        addLabelIntake({
            deviceSerial: deviceSerial,
            label: newLabel
        }, {
            onSuccess: () => {
                refetch();
            },
            onError: (e: Error) => {
                console.error('Upload error', e);
                setErrorMessage('An error occurred while uploading the label. Please try again.'); // Generic error message
            },
        });
    };

    const handlePhoneCall = async () => {
        const result = await initializeDevice();
        if (result) {
            makePhoneCall(result.device, result.identity, result.callerId);
        }
    };

    const hangUpCall = () => {
        if (device) {
            device.disconnectAll();
        }
        recorder.current?.stopRecording();
    };

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedNumber = formatPhoneNumber(e.target.value.replace(/\D/g, ''));
        setPhoneNumber(formattedNumber ? `+1 ${formattedNumber}` : '+1 ');
    };

    const handlePatientChange = (patientId: string) => {
        if (!patientId) {
            setSelectedPatient(null);
            setCurrentPatientId('');
            label.patient = null;
            label.patientId = '';
            setPhoneNumber('+1 ');
            
            if (device) {
                device.disconnectAll();
                device.destroy();
                setDevice(undefined);
            }
            return;
        }

        const patient = patients.find(p => p.id === patientId);
        if (patient) {
            setSelectedPatient(patient);
            setCurrentPatientId(patient.id);
            label.patient = patient;
            label.patientId = patient.id;
            const formattedNumber = patient.phoneNumber ? formatPhoneNumber(patient.phoneNumber) : '';
            setPhoneNumber(formattedNumber ? `+1 ${formattedNumber}` : '+1 ');
            
            if (device) {
                device.disconnectAll();
                device.destroy();
                setDevice(undefined);
            }
        }
    };

    const handleDialPadClick = (num: string) => {
        const currentNumber = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '');
        const newNumber = currentNumber + num;
        const formattedNumber = formatPhoneNumber(newNumber);
        setPhoneNumber(formattedNumber ? `+1 ${formattedNumber}` : '+1 ');
    };

    const handleBackspace = () => {
        const currentNumber = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '');
        const newNumber = currentNumber.slice(0, -1);
        const formattedNumber = formatPhoneNumber(newNumber);
        setPhoneNumber(formattedNumber ? `+1 ${formattedNumber}` : '+1 ');
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (inCall) {
            setRecordingTime(0);
            recordingInterval.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } else {
            if (recordingInterval.current) {
                clearInterval(recordingInterval.current);
                recordingInterval.current = null;
            }
            setRecordingTime(0);
        }

        return () => {
            if (recordingInterval.current) {
                clearInterval(recordingInterval.current);
                recordingInterval.current = null;
            }
        };
    }, [inCall]);

    useEffect(() => {
        if (device) {
            console.log('Device initialized:', device);
        }
    }, [device]);

    useEffect(() => {
        if (!inCall && recordedChunks.length > 0) {
            const blob = new Blob(recordedChunks, { type: 'audio/webm' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            a.href = url;
            a.download = 'recorded_call.webm';
            a.click();
            window.URL.revokeObjectURL(url);
        }
    }, [inCall, recordedChunks]);

    useEffect(() => {
        if (label.patientId) {
            getPatient(label.patientId).then(patient => {
                setSelectedPatient(patient);
                setCurrentPatientId(patient!.id);
                const formattedNumber = patient!.phoneNumber ? formatPhoneNumber(patient!.phoneNumber) : '';
                setPhoneNumber(formattedNumber ? `+1 ${formattedNumber}` : label.metadata?.phoneNumber ?? '+1 ');
            }).catch(error => {
                console.error('Error fetching patient:', error);
                setErrorMessage('An error occurred while fetching patient details. Please try again.');
            });
        }
    }, [label.patientId]);

    // Clean up device on unmount
    useEffect(() => {
        return () => {
            if (device) {
                device.disconnectAll();
                device.destroy();
            }
        };
    }, [device]);

    return (
        <div className="flex flex-col gap-4 w-[400px] bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                <h3 className="text-xl font-semibold text-gray-800">Phone Call</h3>
                {inCall && (
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-red-500 font-medium animate-pulse">
                                Recording
                            </span>
                        </div>
                        <div className="px-2 py-1 bg-gray-100 rounded-md">
                            <span className="text-sm font-mono text-gray-700">{formatTime(recordingTime)}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="space-y-4">
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Select Patient (Optional)</label>
                    <SearchablePatientSelector
                        selectedPatientId={selectedPatient?.id || ''}
                        onPatientSelect={handlePatientChange}
                        placeholder="Search patients..."
                        disabled={inCall}
                        includeDefault={true}
                        defaultOptionText="Use Default Patient"
                        showPatientDetails={true}
                        className=""
                    />
                </div>

                {selectedPatient && (
                    <div className="p-4 bg-gray-50 rounded-md space-y-2 border border-gray-200">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">Patient:</span>
                            <span className="text-sm text-gray-800">{selectedPatient.name || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">Owner:</span>
                            <span className="text-sm text-gray-800">{selectedPatient.ownerLastName || 'N/A'}</span>
                        </div>
                    </div>
                )}

                {errorMessage && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{errorMessage}</p>
                    </div>
                )}

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <div className="flex items-center w-full border border-gray-300 rounded-md shadow-sm focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                        <span className="pl-3 pr-2 text-gray-500 select-none border-r border-gray-300 py-2">+1</span>
                        <input
                            type="text"
                            value={phoneNumber.replace('+1 ', '')}
                            onChange={handlePhoneNumberChange}
                            placeholder="(XXX) XXX-XXXX"
                            className="w-full p-2 border-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500"
                            disabled={inCall}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    {[...Array(9)].map((_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => handleDialPadClick((i + 1).toString())}
                            className="p-3 text-center border border-gray-200 rounded-md transition-colors duration-150
                                     disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                            disabled={inCall}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => handleDialPadClick('0')}
                        className="p-3 text-center border border-gray-200 rounded-md transition-colors duration-150
                                 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                        disabled={inCall}
                    >
                        0
                    </button>
                    <button
                        onClick={handleBackspace}
                        className="p-3 text-center border border-gray-200 rounded-md transition-colors duration-150
                                 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                        disabled={inCall}
                    >
                        ⌫
                    </button>
                </div>

                <div className="flex gap-2 pt-4">
                    <button
                        onClick={handlePhoneCall}
                        disabled={inCall || !phoneNumber.startsWith('+1 ')}
                        className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-md text-white font-medium
                            ${inCall || !phoneNumber.startsWith('+1 ')
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-green-500 hover:bg-green-600 active:bg-green-700'
                            } transition-colors duration-150`}
                    >
                        <FontAwesomeIcon icon="phone" />
                        Call
                    </button>
                    <button
                        onClick={hangUpCall}
                        disabled={!inCall}
                        className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-md text-white font-medium
                            ${!inCall
                                ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-600'
                                : 'bg-red-500 hover:bg-red-600 active:bg-red-700'
                            } transition-colors duration-150`}
                    >
                        <FontAwesomeIcon icon="phone-slash" />
                        Hang Up
                    </button>
                </div>

                <button
                    onClick={() => {
                        if (inCall) {
                            hangUpCall();
                        }
                        cancelAdd();
                    }}
                    className="w-full p-2 text-sm font-medium border border-gray-200 
                             rounded-md transition-colors duration-150"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};
